/**
 * FarmRoadInsightCardMini
 *
 * Carte minimaliste pour l'affichage des analyses dans l'historique
 * Affiche uniquement : secteur, culture, date, période
 */

import React from 'react';
import { Button, Chip, IconButton } from '@mui/material';
import {
  Calendar,
  Clock,
  MapPin,
  Leaf,
  Eye,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  Trash2
} from 'lucide-react';

const FarmRoadInsightCardMini = ({ insight, onConsult, onDelete }) => {
  /**
   * Formatte une date au format court
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  /**
   * Retourne le label de la période
   */
  const getPeriodLabel = (period) => {
    const labels = {
      '24h': '24 heures',
      'daily': 'Journalier',
      'weekly': '7 jours',
      'monthly': '30 jours',
      '3months': '3 mois'
    };
    return labels[period] || period;
  };

  /**
   * Retourne la config de couleur selon le statut
   */
  const getStatusConfig = (status) => {
    switch (status) {
      case 'critical':
        return {
          color: 'error',
          icon: AlertCircle,
          label: 'Critique',
          bgClass: 'bg-red-50',
          borderClass: 'border-red-300'
        };
      case 'warning':
        return {
          color: 'warning',
          icon: AlertTriangle,
          label: 'Avertissement',
          bgClass: 'bg-yellow-50',
          borderClass: 'border-yellow-300'
        };
      case 'optimal':
        return {
          color: 'success',
          icon: CheckCircle,
          label: 'Optimal',
          bgClass: 'bg-green-50',
          borderClass: 'border-green-300'
        };
      case 'acceptable':
        return {
          color: 'info',
          icon: Info,
          label: 'Acceptable',
          bgClass: 'bg-blue-50',
          borderClass: 'border-blue-300'
        };
      default:
        return {
          color: 'default',
          icon: Info,
          label: 'Inconnu',
          bgClass: 'bg-gray-50',
          borderClass: 'border-gray-300'
        };
    }
  };

  const statusConfig = getStatusConfig(insight.overall_status);
  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={`rounded-lg border-2 ${statusConfig.borderClass} ${statusConfig.bgClass} p-4 hover:shadow-lg transition-all duration-200 cursor-pointer relative`}
      onClick={onConsult}
    >
      {/* Bouton supprimer en haut à droite */}
      {onDelete && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#dc2626',
            '&:hover': {
              backgroundColor: '#fee2e2'
            }
          }}
        >
          <Trash2 className="h-4 w-4" />
        </IconButton>
      )}

      {/* En-tête : Secteur + Culture */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="h-4 w-4 text-gray-600" />
          <h3 className="font-semibold text-gray-900 text-sm">
            {insight.sector_name || 'Toute la ferme'}
          </h3>
        </div>

        <div className="flex items-center gap-2">
          <Leaf className="h-4 w-4 text-green-600" />
          <span className="text-sm text-gray-700">
            {insight.culture || 'Framboises'}
          </span>
        </div>
      </div>

      {/* Date de l'analyse */}
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm text-gray-700">
          {formatDate(insight.created_at)}
        </span>
      </div>

      {/* Période analysée */}
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-gray-500" />
        <span className="text-sm text-gray-700">
          {getPeriodLabel(insight.analysis_period)}
        </span>
      </div>

      {/* Badge de statut */}
      <div className="flex items-center justify-between">
        <Chip
          icon={<StatusIcon className="h-3 w-3" />}
          label={statusConfig.label}
          color={statusConfig.color}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        />

        <Button
          size="small"
          variant="outlined"
          startIcon={<Eye className="h-3 w-3" />}
          onClick={(e) => {
            e.stopPropagation();
            onConsult();
          }}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            padding: '4px 12px'
          }}
        >
          Consulter
        </Button>
      </div>
    </div>
  );
};

export default FarmRoadInsightCardMini;
